import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  Optional
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { finalize } from 'rxjs/operators';
import { StatusPageService } from 'src/app/_theme/core/status-page/status-page.service';
import { AuthService } from 'src/app/_theme/core/users/Auth/_services/auth.service';
import { UserstorageService } from 'src/app/_theme/core/users/userstorage/userstorage.service';
import {
  STATUS_PAGE_INTERNAL_SUPPORTED_CONTACT_METHOD_NAMES,
  STATUS_PAGE_SUBSCRIPTION_CHANNEL_OPTIONS
} from '../shared/subscription-dialog.constants';
import {
  AvailableServiceGroup,
  InternalChannelId,
  InternalSubscribeRequest,
  NotificationChannelId,
  NotificationChannelOption,
  PublicServiceItem,
  ServiceSelectionGroup,
  ServiceSelectionOption,
  StatusMainPageSubscriptionDialogData,
  SubscribeDialogStep,
  SubmitNoticeType,
  UserContactMethod
} from '../shared/subscription-dialog.models';
import {
  getChannelLabel,
  resolveSubscriptionSubmitFailure
} from '../shared/subscription-dialog.submit-feedback';
import { SubscriptionServiceSelectionState } from '../shared/subscription-dialog.service-selection';

@Component({
  selector: 'app-status-main-page-internal-subscription-dialog',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatRadioModule
  ],
  templateUrl: './internal-subscription-dialog.component.html',
  styleUrls: [
    '../shared/styles/subscription-dialog.component.scss',
    '../shared/styles/subscription-dialog.component.services-verification.scss',
    '../shared/styles/subscription-dialog.component.theme-panel-overrides.scss',
    '../shared/styles/subscription-dialog.component.theme-panel-overrides.dark.scss',
    '../shared/styles/subscription-dialog.component.theme-panel-overrides.public-dark.scss',
    '../shared/styles/subscription-dialog.component.theme-panel-overrides.light.scss',
    './internal-subscription-dialog.component.manage.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatusMainPageInternalSubscriptionDialogComponent implements OnInit {
  currentStep: SubscribeDialogStep = 'channel';
  servicesLoading = false;
  servicesLoadError = '';
  isSubmitting = false;
  submitError = '';
  submitNoticeType: SubmitNoticeType = 'error';
  availableChannels: NotificationChannelOption[] = [];
  isInternalProfileLoading = false;
  internalChannelError = '';
  internalChannelSelectionMessage = '';
  submittedChannelId: NotificationChannelId = 'email';
  sessionSuccessMessage = '';
  sessionSubscribedChannels: InternalChannelId[] = [];
  readonly serviceSelection = new SubscriptionServiceSelectionState();
  selectedInternalChannels: Record<InternalChannelId, boolean> = {
    email: false,
    sms: false
  };
  private readonly channelOptions = STATUS_PAGE_SUBSCRIPTION_CHANNEL_OPTIONS;

  constructor(
    private readonly dialogRef: MatDialogRef<StatusMainPageInternalSubscriptionDialogComponent>,
    private readonly statusPageService: StatusPageService,
    private readonly authService: AuthService,
    private readonly userStorageService: UserstorageService,
    private readonly _cd: ChangeDetectorRef,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: StatusMainPageSubscriptionDialogData
  ) {}

  ngOnInit(): void {
    if (this.data?.mode === 'completed') {
      this.currentStep = 'completed';
      return;
    }

    this.setupEnabledChannels();
    this.loadInternalUserChannels();
    this.currentStep = 'channel';
    this.loadServiceOptions();
  }

  close(): void {
    this.dialogRef.close();
  }

  get subscribeAllServices(): boolean {
    return this.serviceSelection.subscribeAllServices;
  }

  get serviceTreeTouched(): boolean {
    return this.serviceSelection.serviceTreeTouched;
  }

  get selectedServiceIds(): number[] {
    return this.serviceSelection.selectedServiceIds;
  }

  get totalServiceCount(): number {
    return this.serviceSelection.serviceOptions.length;
  }

  get serviceGroups(): ServiceSelectionGroup[] {
    return this.serviceSelection.serviceGroups;
  }

  get filteredServiceGroups(): ServiceSelectionGroup[] {
    return this.serviceSelection.filteredServiceGroups;
  }

  get ungroupedServiceOptions(): ServiceSelectionOption[] {
    return this.serviceSelection.ungroupedServiceOptions;
  }

  get filteredUngroupedServiceOptions(): ServiceSelectionOption[] {
    return this.serviceSelection.filteredUngroupedServiceOptions;
  }

  get serviceSearchQuery(): string {
    return this.serviceSelection.serviceSearchQuery;
  }

  get hasActiveServiceSearch(): boolean {
    return this.serviceSelection.hasActiveServiceSearch;
  }

  get hasServiceSearchResults(): boolean {
    return this.serviceSelection.hasServiceSearchResults;
  }

  get showCloseOnlyAfterDuplicate(): boolean {
    if (!this.submitError || this.submitNoticeType !== 'info') {
      return false;
    }
    return this.submittedChannelId === 'email' || this.submittedChannelId === 'sms';
  }

  get isSessionManageMode(): boolean {
    return !!this.sessionSuccessMessage;
  }

  get isInternalContinueDisabled(): boolean {
    if (this.isInternalProfileLoading || this.isLimitReached) {
      return true;
    }
    if (this.internalChannelError) {
      return true;
    }
    return this.getSelectedInternalChannelIds().length === 0;
  }

  get isLimitReached(): boolean {
    const max = this.data?.maxSubscribers;
    if (max === null || max === undefined) {
      return false;
    }
    const currentRaw = this.data?.currentSubscribers;
    const current = Number.isFinite(Number(currentRaw)) ? Number(currentRaw) : 0;
    return current >= max;
  }

  get selectedChannelLabels(): string[] {
    const channels = this.isSessionManageMode && this.sessionSubscribedChannels.length
      ? this.sessionSubscribedChannels
      : this.getSelectedInternalChannelIds();
    return channels.map((channelId) => getChannelLabel(this.channelOptions, channelId));
  }

  get currentMode(): 'all' | 'selected' {
    return this.subscribeAllServices ? 'all' : 'selected';
  }

  get modeLocked(): boolean {
    return this.isSubmitting || this.isSessionManageMode;
  }

  get servicesLocked(): boolean {
    return this.isSubmitting || this.isSessionManageMode;
  }

  get modeNote(): string {
    if (this.subscribeAllServices) {
      return `You will receive notifications for all ${this.totalServiceCount} services on this status page.`;
    }
    return `Selected ${this.selectedServiceIds.length} of ${this.totalServiceCount} services.`;
  }

  get limitMessage(): string {
    const max = this.data?.maxSubscribers;
    if (max === null || max === undefined) {
      return '';
    }
    const currentRaw = this.data?.currentSubscribers;
    const current = Number.isFinite(Number(currentRaw)) ? Number(currentRaw) : null;
    if (current === null) {
      return `Maximum subscribers reached (${max}).`;
    }
    return `Maximum subscribers reached (${current}/${max}).`;
  }

  onContinue(): void {
    if (this.isLimitReached) {
      return;
    }
    this.internalChannelSelectionMessage = '';
    if (this.isInternalContinueDisabled) {
      if (!this.isInternalProfileLoading && !this.internalChannelError && this.getSelectedInternalChannelIds().length === 0) {
        this.internalChannelSelectionMessage = 'Select at least one channel to continue.';
      }
      this._cd.markForCheck();
      return;
    }
    this.currentStep = 'service-selection';
    this.clearSubmitFeedback();
    this._cd.markForCheck();
  }

  onBackToChannels(): void {
    if (this.isSessionManageMode) {
      return;
    }
    this.currentStep = 'channel';
    this.clearSubmitFeedback();
  }

  onModeChanged(mode: 'all' | 'selected'): void {
    if (this.modeLocked) {
      return;
    }
    this.serviceSelection.toggleAllServices(mode === 'all');
    this.clearSubmitFeedback();
    this._cd.markForCheck();
  }

  onSubscribe(): void {
    if (this.isSubmitting || this.isLimitReached) {
      return;
    }
    if (this.servicesLoading || this.servicesLoadError) {
      return;
    }
    if (!this.subscribeAllServices && this.selectedServiceIds.length === 0) {
      this.serviceSelection.serviceTreeTouched = true;
      this.clearSubmitFeedback();
      this._cd.markForCheck();
      return;
    }

    const slug = String(this.data?.slug || '').trim();
    if (!slug) {
      this.submitError = 'Missing page context. Please refresh and try again.';
      return;
    }

    const selectedInternalChannelIds = this.getSelectedInternalChannelIds();
    this.submittedChannelId = selectedInternalChannelIds[0] || 'email';
    if (!selectedInternalChannelIds.length) {
      this.submitError = 'Select at least one channel to subscribe.';
      return;
    }

    const payload: InternalSubscribeRequest = {
      subscriberTypes: selectedInternalChannelIds,
      subscribeToAllServices: this.subscribeAllServices,
      serviceIds: this.subscribeAllServices ? [] : this.selectedServiceIds,
      notificationPreferences: ''
    };

    this.clearSubmitFeedback();
    this.isSubmitting = true;
    this.currentStep = 'service-selection';

    this.statusPageService
      .subscribeInternalStatusPageBySlug(slug, payload)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this._cd.markForCheck();
        })
      )
      .subscribe({
        next: (response: any) => {
          const isSuccess = response?.success !== false;
          if (!isSuccess) {
            this.applySubmitFailure(response);
            return;
          }
          this.sessionSubscribedChannels = [...selectedInternalChannelIds];
          this.sessionSuccessMessage = 'Your subscription was created with the selected channels and services.';
          this.currentStep = 'service-selection';
        },
        error: (error) => {
          this.applySubmitFailure(error);
        }
      });
  }

  onToggleAllServices(value: boolean): void {
    if (this.modeLocked) {
      return;
    }
    this.serviceSelection.toggleAllServices(value);
    if (value) {
      this.clearSubmitFeedback();
      this._cd.markForCheck();
    }
  }

  onInternalChannelToggled(channelId: NotificationChannelId, checked: boolean): void {
    if (this.isSessionManageMode) {
      return;
    }
    if (channelId !== 'email' && channelId !== 'sms') {
      return;
    }
    this.selectedInternalChannels[channelId] = checked;
    this.internalChannelSelectionMessage = '';
    this.clearSubmitFeedback();
    this._cd.markForCheck();
  }

  onServiceSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.serviceSelection.setServiceSearchQuery(input?.value || '');
    this._cd.markForCheck();
  }

  clearServiceSearch(): void {
    this.serviceSelection.clearServiceSearch();
    this._cd.markForCheck();
  }

  isInternalChannelChecked(channelId: NotificationChannelId): boolean {
    if (channelId === 'email') {
      return this.selectedInternalChannels.email;
    }
    if (channelId === 'sms') {
      return this.selectedInternalChannels.sms;
    }
    return false;
  }

  trackByChannelId(index: number, channel: NotificationChannelOption): string | number {
    return channel?.id || index;
  }

  trackByServiceSelectionId(index: number, service: ServiceSelectionOption): number {
    return service?.serviceId || index;
  }

  trackByServiceGroupId(index: number, group: ServiceSelectionGroup): string | number {
    return group?.groupId ?? group?.name ?? index;
  }

  toggleService(service: ServiceSelectionOption, checked: boolean): void {
    if (this.servicesLocked) {
      return;
    }
    this.serviceSelection.toggleService(service, checked);
    if (this.selectedServiceIds.length > 0) {
      this.submitError = '';
    }
    this._cd.markForCheck();
  }

  toggleServiceGroup(group: ServiceSelectionGroup, checked: boolean): void {
    if (this.servicesLocked) {
      return;
    }
    this.serviceSelection.toggleServiceGroup(group, checked);
    if (this.selectedServiceIds.length > 0) {
      this.submitError = '';
    }
    this._cd.markForCheck();
  }

  toggleGroupExpansion(group: ServiceSelectionGroup): void {
    this.serviceSelection.toggleGroupExpansion(group);
    this._cd.markForCheck();
  }

  onDone(): void {
    this.close();
  }

  isServiceGroupSelected(group: ServiceSelectionGroup): boolean {
    return this.serviceSelection.isServiceGroupSelected(group);
  }

  isServiceGroupIndeterminate(group: ServiceSelectionGroup): boolean {
    return this.serviceSelection.isServiceGroupIndeterminate(group);
  }

  isServiceSelected(service: ServiceSelectionOption): boolean {
    return this.serviceSelection.isServiceSelected(service);
  }

  private loadServiceOptions(): void {
    this.servicesLoadError = '';
    this.servicesLoading = true;

    const statusPageId = String(this.data?.statusPageId || '').trim();
    if (!statusPageId) {
      this.servicesLoading = false;
      this.servicesLoadError = 'Unable to load services. Missing status page context.';
      this.serviceSelection.initialize([], []);
      this._cd.markForCheck();
      return;
    }

    this.statusPageService
      .getInternalSubscriberManage(statusPageId)
      .pipe(
        finalize(() => {
          this.servicesLoading = false;
          this._cd.markForCheck();
        })
      )
      .subscribe({
        next: (response: any) => {
          const normalized = this.normalizeManageAvailableServices(response?.availableServices);
          const selectedServiceIds = this.resolveManageSelectedServiceIds(response, normalized.services);
          this.serviceSelection.initialize(
            normalized.services,
            normalized.groups,
            {
              subscribeAllServices: response?.subscribedToAllServices === true,
              selectedServiceIds
            }
          );
        },
        error: () => {
          this.servicesLoadError = 'Unable to load services. Please try again in a moment.';
          this.serviceSelection.initialize([], []);
        }
      });
  }

  private normalizeManageAvailableServices(availableServices: any): {
    services: PublicServiceItem[];
    groups: AvailableServiceGroup[];
  } {
    const services: PublicServiceItem[] = [];
    const groups = new Map<string, AvailableServiceGroup & { displayOrder: number; services: PublicServiceItem[] }>();

    (Array.isArray(availableServices) ? availableServices : []).forEach((item, index) => {
      const serviceId = Number(item?.serviceId);
      const name = String(item?.name || '').trim();
      if (!Number.isFinite(serviceId) || serviceId <= 0 || !name) {
        return;
      }

      const service: PublicServiceItem = {
        serviceId,
        name,
        description: String(item?.description || '').trim(),
        displayOrder: index,
        groupId: item?.groupId ?? null,
        groupName: item?.groupName ? String(item.groupName).trim() : null,
        isSubscribed: item?.isSubscribed === true
      };
      services.push(service);

      const groupName = String(service.groupName || '').trim();
      if (!groupName) {
        return;
      }

      const groupId = service.groupId ?? groupName;
      const key = `${groupId}::${groupName.toLowerCase()}`;
      if (!groups.has(key)) {
        groups.set(key, {
          groupId,
          name: groupName,
          displayOrder: index,
          services: []
        });
      }

      groups.get(key)!.services.push(service);
    });

    return {
      services,
      groups: Array.from(groups.values()).sort(
        (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name)
      )
    };
  }

  private resolveManageSelectedServiceIds(response: any, services: PublicServiceItem[]): number[] {
    const isSubscribedIds = services
      .filter((service) => service?.isSubscribed === true)
      .map((service) => Number(service?.serviceId))
      .filter((serviceId) => Number.isFinite(serviceId) && serviceId > 0);

    if (isSubscribedIds.length > 0) {
      return isSubscribedIds;
    }

    return (Array.isArray(response?.subscribedServiceIds) ? response.subscribedServiceIds : [])
      .map((serviceId: any) => Number(serviceId))
      .filter((serviceId: number) => Number.isFinite(serviceId) && serviceId > 0);
  }

  private setupEnabledChannels(): void {
    const normalize = (value: any): string =>
      String(value ?? '')
        .trim()
        .toLowerCase()
        .replaceAll(/[\s_-]+/g, '');

    const enabledList = (this.data?.enabledChannels || [])
      .map((channel) => normalize(channel))
      .filter((value) => !!value);
    const enabled = new Set(enabledList);
    const isEmailEnabled = enabled.has('email') || enabled.has('emailofficial');
    const isSmsEnabled =
      enabled.has('sms') ||
      enabled.has('text') ||
      enabled.has('textmessage') ||
      enabled.has('smsofficial');

    const pageEnabledInternalChannelIds = new Set<InternalChannelId>();
    if (isEmailEnabled) {
      pageEnabledInternalChannelIds.add('email');
    }
    if (isSmsEnabled) {
      pageEnabledInternalChannelIds.add('sms');
    }

    this.availableChannels = this.channelOptions.filter((channel) =>
      (channel.id === 'email' || channel.id === 'sms') &&
      pageEnabledInternalChannelIds.has(channel.id)
    );
  }

  private loadInternalUserChannels(): void {
    this.internalChannelError = '';
    this.internalChannelSelectionMessage = '';
    this.selectedInternalChannels = { email: false, sms: false };

    if (!this.availableChannels.length) {
      this.internalChannelError = 'This status page does not allow Email or SMS subscriptions.';
      this._cd.markForCheck();
      return;
    }

    const token = this.getToken();
    const userId = this.getUserId();
    if (!token || !userId) {
      this.internalChannelError = 'Unable to identify your account. Please sign in again.';
      this._cd.markForCheck();
      return;
    }

    this.isInternalProfileLoading = true;
    this.authService.getUserById(token, userId)
      .pipe(
        finalize(() => {
          this.isInternalProfileLoading = false;
          this._cd.markForCheck();
        })
      )
      .subscribe({
        next: (userInfo: any) => {
          const userEligibleChannels = this.extractInternalEligibleChannelsFromContactMethods(userInfo?.contact_methods);
          this.availableChannels = this.availableChannels.filter((channel) =>
            (channel.id === 'email' || channel.id === 'sms') &&
            userEligibleChannels.has(channel.id)
          );
          if (!this.availableChannels.length) {
            this.internalChannelError = 'No eligible contact methods found. Add Email-Official or SMS-Official to subscribe.';
            return;
          }

          this.selectedInternalChannels.email = this.availableChannels.some((channel) => channel.id === 'email');
          this.selectedInternalChannels.sms = this.availableChannels.some((channel) => channel.id === 'sms');
        },
        error: () => {
          this.internalChannelError = 'Unable to load contact methods. Please try again in a moment.';
        }
      });
  }

  private getSelectedInternalChannelIds(): InternalChannelId[] {
    const channels: InternalChannelId[] = [];
    if (this.selectedInternalChannels.email) {
      channels.push('email');
    }
    if (this.selectedInternalChannels.sms) {
      channels.push('sms');
    }
    return channels;
  }

  private extractInternalEligibleChannelsFromContactMethods(contactMethods: UserContactMethod[]): Set<InternalChannelId> {
    const eligible = new Set<InternalChannelId>();
    const methods = Array.isArray(contactMethods) ? contactMethods : [];
    methods.forEach((method) => {
      const name = String(method?.contact_method_name || '').trim().toLowerCase();
      if (method?.enabled === false) {
        return;
      }
      if (name === STATUS_PAGE_INTERNAL_SUPPORTED_CONTACT_METHOD_NAMES.email) {
        const emailAddress = String(method?.email?.email_address || '').trim();
        if (emailAddress) {
          eligible.add('email');
        }
      }
      if (name === STATUS_PAGE_INTERNAL_SUPPORTED_CONTACT_METHOD_NAMES.sms) {
        const phoneNumber = String(method?.sms?.phone_number || '').trim();
        if (phoneNumber) {
          eligible.add('sms');
        }
      }
    });
    return eligible;
  }

  private getToken(): string {
    return String(this.userStorageService.getToken() || '').trim();
  }

  private getUserId(): string {
    const stored = this.userStorageService.getuserstorage();
    if (!stored) {
      return '';
    }
    try {
      const parsed = JSON.parse(stored);
      return String(parsed?.user_id || '').trim();
    } catch {
      return '';
    }
  }

  private clearSubmitFeedback(): void {
    this.submitError = '';
    this.submitNoticeType = 'error';
    this.sessionSuccessMessage = '';
  }

  private applySubmitFailure(errorOrMessage: any): void {
    const resolved = resolveSubscriptionSubmitFailure(
      errorOrMessage,
      this.getSelectedInternalChannelLabels()
    );
    this.submitError = resolved.message;
    this.submitNoticeType = resolved.type;
    this.currentStep = 'service-selection';
  }

  private getSelectedInternalChannelLabels(): string {
    const labels = this.getSelectedInternalChannelIds()
      .map((channelId) => getChannelLabel(this.channelOptions, channelId))
      .filter((label) => !!label);
    if (!labels.length) {
      return 'the selected channels';
    }
    return labels.join(', ');
  }
}
